import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userSecret, isRegistered, setUserId, setUserName, setRegistered } = useUserStore();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      // If on register page, don't check
      if (location.pathname === '/register') {
        setChecking(false);
        return;
      }

      // If already registered in local state, we're good
      if (isRegistered) {
        setChecking(false);
        return;
      }

      try {
        const response = await fetch('/api/users/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userSecret }),
        });

        const data = await response.json();

        if (data.exists) {
          // User found on server, update local state
          setUserId(data.userId);
          setUserName(data.userName);
          setRegistered(true);
        } else {
          // User not registered, redirect to register
          navigate('/register', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Failed to check user:', error);
        // On error, redirect to register to be safe
        navigate('/register', { replace: true });
        return;
      }

      setChecking(false);
    };

    checkUser();
  }, [userSecret, isRegistered, navigate, location.pathname, setUserId, setUserName, setRegistered]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return <>{children}</>;
}
